import { useMemo } from 'react';
import { Form } from 'react-final-form';
import { useNavigate } from 'react-router-dom';

import cx from 'classnames';

import { datadogLogs } from '@datadog/browser-logs';

import { Button } from '@motorway/mw-highway-code';

import { useFetch, useReport, REPORT_OUTCOMES } from '@motorway/motorway-handshake-components';
import { useVehicle } from '@motorway/motorway-handshake-components';
import type { ReportInspection } from '@motorway/motorway-handshake-components/types';

import { BackButton } from '../../components/BackButton/BackButton';
import { FETCH_ERROR_MESSAGE } from '../../components/forms/forms.helpers';
import { Images } from '../../components/forms/Images/Images';
import { InputNumericFormat } from '../../components/forms/InputNumericFormat';
import { Select } from '../../components/forms/Select';
import { Textarea } from '../../components/forms/Textarea';
import { Header } from '../../components/Header/Header';
import { debouncedAlert } from '../../helpers/debouncedAlert';
import { ROUTES } from '../../helpers/routes';
import { snowplowCtaClick } from '../../helpers/snowplow';
import {
  ADJUSTMENT_QUESTION,
  CANCELLATION_QUESTION_KEY,
  CANCELLATION_REASON_OPTIONS,
  FORM_KEYS,
  NEEDS_ON_SITE_SUPPORT_QUESTION_KEY,
  PRICE_ADJUSTMENT_REASON_OPTIONS,
  YES_NO_OPTIONS,
} from './Adjustment.helpers';

import styles from './Adjustment.module.scss';

const isAdjustment = (outcome?: ReportInspection['outcome']) => outcome === REPORT_OUTCOMES.ADJUSTMENT;

type BooleanFormValue = '' | 'false' | 'true';
const EMPTY_BOOLEAN_FORM_VALUE: BooleanFormValue = '';
type FormValues = Partial<
  Omit<ReportInspection, 'needsOnSiteSupport' | 'priceAdjustmentAttempted' | 'proposedPrice'>
  & {
    needsOnSiteSupport: BooleanFormValue;
    priceAdjustmentAttempted: BooleanFormValue;
    proposedPrice: string;
  }
>;

const formatBoolean = (value?: BooleanFormValue) => {
  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return undefined;
};

const formatFormValues = (values: FormValues, IS_ADJUSTMENT: boolean) => {
  const {
    needsOnSiteSupport,
    priceAdjustmentAttempted,
    proposedPrice,
    ...remainingValues
  } = values;

  const formattedNeedsOnSiteSupport = formatBoolean(needsOnSiteSupport);
  const formattedPriceAdjustmentAttempted = formatBoolean(priceAdjustmentAttempted);

  return {
    ...remainingValues,
    ...(proposedPrice
      ? { [ADJUSTMENT_QUESTION.ADJUSTMENT_AMOUNT]: Number(proposedPrice.replaceAll(',', '')) }
      : {}),
    ...(formattedNeedsOnSiteSupport !== undefined
      ? { [NEEDS_ON_SITE_SUPPORT_QUESTION_KEY]: formattedNeedsOnSiteSupport }
      : {}),
    ...(!IS_ADJUSTMENT && formattedPriceAdjustmentAttempted !== undefined
      ? { [CANCELLATION_QUESTION_KEY]: formattedPriceAdjustmentAttempted }
      : {}),
  };
};

const initialValues = (report: Partial<ReportInspection> | undefined): FormValues => {
  const formKeys = Object.values(FORM_KEYS);
  const inspection = report ?? {};
  const { outcome } = inspection;

  const mappedKeys = formKeys.reduce<Record<string, string>>((acc, k) => {
    const value = inspection[k];
    if (value !== undefined && value !== null) {
      acc[k] = String(value);
    }
    return acc;
  }, {});

  const PRICE_ADJUSTMENT = inspection[ADJUSTMENT_QUESTION.ADJUSTMENT_AMOUNT];
  if (PRICE_ADJUSTMENT) {
    mappedKeys[ADJUSTMENT_QUESTION.ADJUSTMENT_AMOUNT] = String(PRICE_ADJUSTMENT);
  }

  const CANCELLATION_ATTEMPTED = inspection[CANCELLATION_QUESTION_KEY];

  if (CANCELLATION_ATTEMPTED !== undefined) {
    mappedKeys[CANCELLATION_QUESTION_KEY] = String(CANCELLATION_ATTEMPTED);
  }

  const IS_ADJUSTMENT = isAdjustment(outcome);
  const ADJUSTMENT_REASON = IS_ADJUSTMENT
    ? PRICE_ADJUSTMENT_REASON_OPTIONS
    : CANCELLATION_REASON_OPTIONS;

  return {
    [CANCELLATION_QUESTION_KEY]: EMPTY_BOOLEAN_FORM_VALUE,
    [FORM_KEYS.ADJUSTMENT_REASON]: ADJUSTMENT_REASON[0]?.value,
    [FORM_KEYS.ADJUSTMENT_SPECIFIC]: ADJUSTMENT_REASON[0]?.children?.[0],
    [NEEDS_ON_SITE_SUPPORT_QUESTION_KEY]: EMPTY_BOOLEAN_FORM_VALUE,
    ...mappedKeys,
  };
};

const AdjustmentForm = () => {
  const { getReport, patchReport, submitReport } = useFetch();
  const { report, setReport } = useReport();
  const { inspection } = report;
  const { outcome } = inspection;
  const { vehicle: { enquiry } } = useVehicle();
  const { value } = enquiry.closingBid;

  const IS_ADJUSTMENT = isAdjustment(outcome);

  const originalReport = useMemo(() => initialValues(inspection), [inspection]);

  const navigate = useNavigate();

  const BUTTON_LABEL = 'Next';

  const onSubmit = async (values: FormValues) => {
    if (!IS_ADJUSTMENT) {
      const confirmCancellation = confirm('Are you sure you want to submit this cancellation?');

      if (!confirmCancellation) {
        return;
      }
    }

    const NEW_ROUTE = IS_ADJUSTMENT
      ? ROUTES.SEND_REPORT
      : ROUTES.CANCELLED;

    snowplowCtaClick({
      button_label: BUTTON_LABEL,
      label: outcome,
      name: 'adjustment_outcome_next',
      url: NEW_ROUTE,
    });

    const formattedValues = formatFormValues(values, IS_ADJUSTMENT);

    const { success } = await patchReport({ reportData: { ...formattedValues, outcome } });

    if (!success) {
      datadogLogs.logger.error('Report patch failed', { reportData: { ...formattedValues, outcome } });
      debouncedAlert(FETCH_ERROR_MESSAGE);
      return;
    }

    const { success: getReportSuccess, data: reportData } = await getReport();

    if (!getReportSuccess) {
      datadogLogs.logger.error('Get report failed', { reportData: { ...formattedValues, outcome } });
      debouncedAlert(FETCH_ERROR_MESSAGE);
      return;
    }

    setReport((prev) => {
      if (!prev) {
        return reportData;
      }

      return {
        ...prev,
        inspection: reportData.inspection,
        images: reportData.images,
      };
    });

    if (IS_ADJUSTMENT) {
      navigate(NEW_ROUTE);
      return;
    }

    const { success: submitSuccess } = await submitReport();

    if (!submitSuccess) {
      datadogLogs.logger.error('Report submit failed', { reportData: { ...formattedValues, outcome } });
      debouncedAlert(FETCH_ERROR_MESSAGE);
      return;
    }

    navigate(NEW_ROUTE, { replace: true });
  };

  const TITLE = IS_ADJUSTMENT ? 'Price adjustment' : 'Cancellation';
  const FIRST_QUESTION = IS_ADJUSTMENT
    ? <InputNumericFormat name={ADJUSTMENT_QUESTION.ADJUSTMENT_AMOUNT} label="Adjustment amount" max={value * 0.75} />
    : (
        <Select
          name={CANCELLATION_QUESTION_KEY}
          label="Was a price adjustment attempted?"
          data={YES_NO_OPTIONS}
        />
      );

  const ADJUSTMENT_REASON = IS_ADJUSTMENT
    ? PRICE_ADJUSTMENT_REASON_OPTIONS
    : CANCELLATION_REASON_OPTIONS;

  return (
    <>
      <BackButton />
      <div className={styles.component}>
        <Header>
          <h1>{TITLE}</h1>
        </Header>
        <Form
          onSubmit={onSubmit}
          initialValues={originalReport}
          render={({ handleSubmit, submitting, valid, values }) => (
            <form onSubmit={handleSubmit} className={cx({ [styles.submitting]: submitting })}>
              {FIRST_QUESTION}
              <Select
                name={NEEDS_ON_SITE_SUPPORT_QUESTION_KEY}
                label="Do you need on-site support?"
                data={YES_NO_OPTIONS}
              />
              <Select name={FORM_KEYS.ADJUSTMENT_REASON} label="Main reason" data={[...ADJUSTMENT_REASON]} />
              <Select
                name={FORM_KEYS.ADJUSTMENT_SPECIFIC}
                label="Specific"
                data={
                  ADJUSTMENT_REASON.find(option => option.value === values?.[FORM_KEYS.ADJUSTMENT_REASON])?.children ?? []
                }
              />
              <Textarea name={FORM_KEYS.ADJUSTMENT_DESCRIPTION} label="Description" />
              <Images />
              <section>
                <Button
                  disabled={!valid || submitting}
                  icon="arrow"
                  label={BUTTON_LABEL}
                  loading={submitting}
                  type="submit"
                />
              </section>
            </form>
          )}
        />
      </div>
    </>
  );
};

export const Adjustment = () => {
  return <AdjustmentForm />;
};
